import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const SEO = ({ title, description, keywords, ogImage }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  
  // Mapping i18next language codes to OG locale format (e.g., hu -> hu_HU)
  const localeMap = {
    'hu': 'hu_HU',
    'en': 'en_US',
    'ro': 'ro_RO',
    'de': 'de_DE',
    'fr': 'fr_FR',
    'es': 'es_ES',
    'it': 'it_IT'
  };

  const supportedLanguages = Object.keys(localeMap);
  const currentLang = i18n.language || 'hu';
  const langCode = currentLang.split('-')[0];
  const ogLocale = localeMap[langCode] || 'hu_HU';

  const seoTitle = title || t('SEO.Title');
  const seoDescription = description || t('SEO.Description');
  const seoKeywords = keywords || t('SEO.Keywords');
  const seoImage = ogImage || "https://szno.ro/populiathyleregis.png";

  const baseUrl = "https://szno.ro";
  const currentPath = location.pathname;
  
  // Helper to get path without language prefix
  const getCleanPath = (path) => {
    const parts = path.split('/').filter(p => p !== "");
    if (parts.length > 0 && supportedLanguages.includes(parts[0])) {
      return '/' + parts.slice(1).join('/');
    }
    return path === "" ? "/" : path;
  };

  const cleanPath = getCleanPath(currentPath);
  const canonicalUrl = `${baseUrl}/${langCode}${cleanPath === '/' ? '' : cleanPath}`;

  // Structured Data (JSON-LD) for Google Sitelinks
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Székely Nemzet Online",
    "alternateName": "SZNO",
    "url": baseUrl,
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${baseUrl}/terkep?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  const organizationData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Székely Nemzet Online",
    "url": baseUrl,
    "logo": "https://szno.ro/populiathyleregis.png",
    "sameAs": [
      "https://www.facebook.com/SzekelyNemzetOnline",
      "https://www.facebook.com/szekelynemzetipart"
    ]
  };

  return (
    <Helmet>
      <html lang={langCode} />
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      
      {/* Multi-language alternate links (hreflang) */}
      {supportedLanguages.map(lng => (
        <link 
          key={lng}
          rel="alternate" 
          hrefLang={lng} 
          href={`${baseUrl}/${lng}${cleanPath === '/' ? '' : cleanPath}`} 
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${cleanPath}`} />

      {/* Structured Data Scripts */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(organizationData)}
      </script>
    </Helmet>
  );
};

export default SEO;
